import { useCallback, useState } from 'react';

export interface DiscardGuard {
	/** Whether the "discard changes?" confirmation should be shown. */
	asking: boolean;
	/**
	 * Call instead of closing: closes right away when nothing changed,
	 * otherwise asks first.
	 */
	requestClose: () => void;
	/** Confirmation answers, for `UnsavedChangesDialog`. */
	stay: () => void;
	discard: () => void;
}

/**
 * Close guard for a create/edit dialog or sheet: closing (Cancel, ×, Escape,
 * outside click) with unsaved changes asks before throwing them away.
 */
export function useDiscardGuard(
	dirty: boolean,
	close: () => void,
): DiscardGuard {
	const [asking, setAsking] = useState(false);

	const requestClose = useCallback(() => {
		if (dirty) setAsking(true);
		else close();
	}, [dirty, close]);

	const stay = useCallback(() => setAsking(false), []);

	const discard = useCallback(() => {
		setAsking(false);
		close();
	}, [close]);

	return { asking, requestClose, stay, discard };
}
